import React, { useEffect, useState } from 'react';
import { sortByJobNumber } from '../../utils/jobOrder';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import AdminLayout from './AdminLayout';
import { jobName } from '../../utils/ui';
import ProjectsTable, { jobNumber } from './ProjectsTable';

// Fix Leaflet's default marker icon (broken by webpack asset handling).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) { map.setView(points[0], 12); return; }
    map.fitBounds(points, { padding: [50, 50], maxZoom: 13 });
  }, [points, map]);
  return null;
}

// Teardrop pin with a short label, colored by kind (project vs equipment).
function pinIcon(color, label) {
  return L.divIcon({
    className: 'np-pin',
    html: `<div style="background:${color};color:#fff;min-width:22px;height:22px;padding:0 5px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 5px rgba(0,0,0,0.35);border:2px solid #fff;"><span style="transform:rotate(45deg);font-weight:800;font-size:11px;white-space:nowrap;">${label}</span></div>`,
    iconSize: [24, 24], iconAnchor: [12, 24], popupAnchor: [0, -22],
  });
}

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken(), ...opts.headers },
  }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}

// Pulled-out map of every active project (that has a saved location), with an
// optional layer showing where the equipment is.
function ProjectsMap({ onBack, pmNames }) {
  const [projects, setProjects] = useState([]);
  const [equip, setEquip] = useState([]);
  const [showEquip, setShowEquip] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiFetch('/projects-map').catch(() => []),
      apiFetch('/equipment-locations').catch(() => ({ items: [] })),
    ]).then(([pm, eq]) => {
      setProjects(Array.isArray(pm) ? pm : []);
      setEquip(Array.isArray(eq?.items) ? eq.items : []);
    }).finally(() => setLoading(false));
  }, []);

  const mapped = projects.filter(p => p.lat != null && p.lng != null);
  const unmapped = projects.length - mapped.length;
  const pmName = p => pmNames[p.pm_id] || '';

  // Equipment grouped onto one pin per location.
  const eqMapped = equip.filter(i => i.mapped && i.lat != null && i.lng != null);
  const eqGroups = {};
  eqMapped.forEach(i => {
    const k = `${i.lat.toFixed(5)},${i.lng.toFixed(5)}`;
    (eqGroups[k] = eqGroups[k] || { lat: i.lat, lng: i.lng, at_yard: i.at_yard, label: i.location_label, items: [] }).items.push(i);
  });
  const eqList = Object.values(eqGroups);

  const points = [...mapped.map(p => [p.lat, p.lng]), ...(showEquip ? eqList.map(g => [g.lat, g.lng]) : [])];
  const center = points[0] || [39.5296, -119.8138]; // Reno
  const assetLine = i => `${i.asset_number ? i.asset_number + ' · ' : ''}${[i.year, i.make, i.model].filter(Boolean).join(' ') || i.name}`;

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 6 }}>‹ Projects list</button>
          <h1 className="admin-page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span>🗺️</span> Project Map</h1>
          <p className="admin-page-sub">{mapped.length} project{mapped.length !== 1 ? 's' : ''} mapped{unmapped ? ` · ${unmapped} without a location` : ''}</p>
        </div>
      </div>

      <div className="admin-card" style={{ overflow: 'hidden', padding: 0 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, color: '#6b7280', flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: '#1e3a5f', display: 'inline-block' }} /> Project</span>
          {showEquip && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: '#7c3aed', display: 'inline-block' }} /> Equipment</span>}
          <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 600, color: '#374151' }}>
            <input type="checkbox" checked={showEquip} onChange={e => setShowEquip(e.target.checked)} /> Show equipment
          </label>
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading map…</div>
        ) : (
          <div style={{ height: '68vh', minHeight: 460, width: '100%' }}>
            <MapContainer center={center} zoom={10} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
              <FitBounds points={points} />
              {mapped.map(p => (
                <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon('#1e3a5f', jobNumber(p))}>
                  <Popup>
                    <div style={{ minWidth: 170 }}>
                      <div style={{ fontWeight: 800, fontSize: 13, color: '#1e3a5f' }}>{p.name}</div>
                      {p.client_name && p.client_name !== p.name && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{p.client_name}</div>}
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>PM: {pmName(p) || '—'}</div>
                      {p.address && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{p.address}</div>}
                    </div>
                  </Popup>
                </Marker>
              ))}
              {showEquip && eqList.map((g, idx) => (
                <Marker key={'eq' + idx} position={[g.lat, g.lng]} icon={pinIcon(g.at_yard ? '#475569' : '#7c3aed', String(g.items.length))}>
                  <Popup>
                    <div style={{ minWidth: 170 }}>
                      <div style={{ fontWeight: 800, fontSize: 13, color: '#7c3aed' }}>🔧 {g.label}</div>
                      {g.items.map(i => (
                        <div key={i.id} style={{ fontSize: 12, color: '#374151', padding: '3px 0', borderTop: '1px solid #f3f4f6' }}>{assetLine(i)}</div>
                      ))}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default function AdminNewProjects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [pmNames, setPmNames]   = useState({}); // user id -> "First Last"
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [divFilter, setDivFilter] = useState('all');
  const [favIds, setFavIds]     = useState(() => new Set());
  const [favOnly, setFavOnly]   = useState(false);
  const [pmFilter, setPmFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // active | complete | closed | all
  const [showMap, setShowMap]   = useState(false);
  const [usaByProject, setUsaByProject] = useState({}); // project_id -> { status, count }
  const [liveCounts, setLiveCounts] = useState({}); // project_id -> { crew, equipment }

  useEffect(() => {
    load();
    apiFetch('/project-favorites').then(ids => setFavIds(new Set(Array.isArray(ids) ? ids : []))).catch(() => {});
    apiFetch('/usa-status-by-project').then(m => setUsaByProject(m && typeof m === 'object' ? m : {})).catch(() => {});
    apiFetch('/projects/live-counts').then(m => setLiveCounts(m && typeof m === 'object' ? m : {})).catch(() => {});
  }, []);

  async function toggleFavorite(e, id) {
    e.stopPropagation();
    const isFav = favIds.has(id);
    setFavIds(prev => { const n = new Set(prev); if (isFav) n.delete(id); else n.add(id); return n; });
    try { await apiFetch(`/project-favorites/${id}`, { method: isFav ? 'DELETE' : 'POST' }); }
    catch { setFavIds(prev => { const n = new Set(prev); if (isFav) n.add(id); else n.delete(id); return n; }); }
  }

  // Executives/Admins can delete a job from the list (mirrors the Bids list).
  const myRole = (() => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role; } catch { return null; } })();
  const canDelete = ['Executive', 'Admin', 'admin'].includes(myRole);
  // Active / Complete / Closed from the list itself — same roles as on the job banner.
  const canSetStatus = ['Executive', 'Admin', 'admin', 'ProjectManager', 'Accounting', 'OfficeStaff'].includes(myRole);
  async function setJobStatus(p, next) {
    if (next === (p.status || 'active')) return;
    if (next === 'closed' && !window.confirm(`Close ${p.project_number || 'this job'}? It comes off the active lists, the field's job pickers and the dashboards. You can reopen it from the Closed filter any time.`)) return;
    const before = p.status;
    setProjects(prev => prev.map(x => x.id === p.id ? { ...x, status: next } : x));
    try { await apiFetch(`/projects/${p.id}`, { method: 'PATCH', body: JSON.stringify({ status: next }) }); }
    catch (e) { setProjects(prev => prev.map(x => x.id === p.id ? { ...x, status: before } : x)); alert(e?.error || 'Could not change the status.'); }
  }
  async function deleteJob(e, p) {
    e.stopPropagation();
    if (!window.confirm(`Delete job ${jobNumber(p)} — ${jobName(p)}?\n\nThis removes the job (crew, sub-jobs, schedule links). Its bid returns to the Bids list so you can re-use it. This cannot be undone.`)) return;
    try {
      await apiFetch(`/projects/${p.id}`, { method: 'DELETE' });
      setProjects(prev => prev.filter(x => x.id !== p.id));
    } catch (err) { alert('Delete failed: ' + (err?.message || err?.error || 'Please try again.')); }
  }

  async function load() {
    setLoading(true);
    try {
      // Fetch all bids that have been awarded (have a project_id) + the user list for PM names
      const [bids, users] = await Promise.all([
        apiFetch('/bids'),
        apiFetch('/users').catch(() => []),
      ]);
      const nameMap = {};
      (Array.isArray(users) ? users : []).forEach(u => { nameMap[u.id] = `${u.firstName || ''} ${u.lastName || ''}`.trim(); });
      setPmNames(nameMap);
      const awarded = Array.isArray(bids) ? bids.filter(b => b.project_id && b.status === 'awarded') : [];
      // Fetch the project for each awarded bid
      const projects = await Promise.all(
        awarded.map(b => apiFetch(`/projects/${b.project_id}`).then(p => ({ ...p, bid: b })).catch(() => null))
      );
      setProjects(projects.filter(Boolean).filter(p => p.status !== 'placeholder'));
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  // Assigned PM = project.pm_id, falling back to the linked bid's bid_pm_id.
  const pmName = p => pmNames[p.pm_id || p.bid?.bid_pm_id] || '';

  // Job-number order by default. The API hands them back newest-created first,
  // which doesn't track the job number and so reads as no order at all.
  // Typing in the search looks through EVERY status (Pat, Oct 7 2026: WO26088 "why can't we see it
  // when we search" — the list was on Active, and a finished job vanished even by its own number).
  // "26088" also finds "WO26088"; spaces and dashes don't matter.
  const q = search.trim().toLowerCase();
  const squash = v => String(v || '').toLowerCase().replace(/[\s\-–—]+/g, '');
  const matchesSearch = p => !q || (p.name || '').toLowerCase().includes(q) ||
    squash(p.project_number).includes(squash(q)) ||
    (p.client_name || '').toLowerCase().includes(q) ||
    pmName(p).toLowerCase().includes(q);
  const passes = (p, ignore = {}) => {
    const st = p.status || 'active';
    return matchesSearch(p)
      && (ignore.div || divFilter === 'all' || p.division === divFilter)
      && (ignore.fav || !favOnly || favIds.has(p.id))
      && (ignore.pm || !pmFilter || pmName(p) === pmFilter)
      && (q || statusFilter === 'all' || st === statusFilter);
  };
  const filtered = sortByJobNumber(projects).filter(p => passes(p));
  // Matches the other filters are hiding (Work Orders / Large Jobs, PM, ★ Favorites).
  const hiddenByFilters = q ? projects.filter(p => !passes(p) && passes(p, { div: true, fav: true, pm: true })).length : 0;
  const clearFilters = () => { setDivFilter('all'); setFavOnly(false); setPmFilter(''); };
  const statusCount = k => projects.filter(p => (p.status || 'active') === k).length;

  const large = projects.filter(p => p.division !== 'small').length;
  const small = projects.filter(p => p.division === 'small').length;
  // Distinct PMs present on the projects, for the PM filter pill.
  const pmOptions = [...new Set(projects.map(pmName).filter(Boolean))].sort();

  if (showMap) return <ProjectsMap onBack={() => setShowMap(false)} pmNames={pmNames} />;

  return (
    <AdminLayout>
      <div className="admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 className="admin-page-title">Projects</h1>
          <p className="admin-page-sub">{projects.length} total · {large} large jobs · {small} work orders</p>
        </div>
        <button onClick={() => setShowMap(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          🗺️ Map View
        </button>
      </div>

      <div className="admin-card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 10, padding: '14px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text" placeholder="Search by job name, number, or client…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200, border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}
          />
          {[['all', 'All'], ['large', 'Large Jobs'], ['small', 'Work Orders']].map(([val, label]) => (
            <button key={val} onClick={() => setDivFilter(val)}
              className={`role-tab ${divFilter === val ? 'active' : ''}`} style={{ fontSize: 12 }}>
              {label}
            </button>
          ))}
          <button onClick={() => setFavOnly(v => !v)}
            style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
              border: `1px solid ${favOnly ? '#f59e0b' : '#e5e7eb'}`,
              background: favOnly ? '#fffbeb' : '#fff', color: favOnly ? '#b45309' : '#6b7280',
              display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ color: '#f59e0b' }}>★</span> Favorites{favIds.size ? ` (${favIds.size})` : ''}
          </button>
          <select value={pmFilter} onChange={e => setPmFilter(e.target.value)}
            title="Filter by Project Manager"
            style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
              border: `1px solid ${pmFilter ? '#1e3a5f' : '#e5e7eb'}`,
              background: pmFilter ? '#eff6ff' : '#fff', color: pmFilter ? '#1e3a5f' : '#6b7280' }}>
            <option value="">All PMs</option>
            {pmOptions.map(nm => <option key={nm} value={nm}>{nm}</option>)}
          </select>
          {/* The job's own status. Active is the working list; Complete and Closed are
              where finished jobs go rather than vanishing. */}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            title="Show jobs by status"
            style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
              border: `1px solid ${statusFilter !== 'active' ? '#15803d' : '#e5e7eb'}`,
              background: statusFilter !== 'active' ? '#f0fdf4' : '#fff', color: statusFilter !== 'active' ? '#15803d' : '#6b7280' }}>
            <option value="active">Active ({statusCount('active')})</option>
            <option value="complete">Complete ({statusCount('complete')})</option>
            <option value="closed">Closed ({statusCount('closed')})</option>
            <option value="all">All statuses</option>
          </select>
        </div>

        {!loading && q && (
          <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--border)', fontSize: 12.5, color: '#6b7280', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span>Searching every status — Active, Complete and Closed.</span>
            {hiddenByFilters > 0 && (
              <button onClick={clearFilters} style={{ background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                {hiddenByFilters} more hidden by your filters — show {hiddenByFilters === 1 ? 'it' : 'them'}
              </button>
            )}
          </div>
        )}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            {favOnly ? 'No favorites yet — tap the ☆ star on any project to add it here.' : projects.length === 0 ? 'No awarded projects yet. Award a bid to create one.' : 'No projects match your search.'}
          </div>
        ) : (
          <ProjectsTable
            rows={filtered}
            onOpen={p => navigate(`/admin/new-projects/${p.id}`)}
            favIds={favIds}
            onToggleFavorite={toggleFavorite}
            usaByProject={usaByProject}
            liveCounts={liveCounts}
            showPM
            pmName={pmName}
            canDelete={canDelete}
            onDelete={deleteJob}
            onStatusChange={canSetStatus ? setJobStatus : null}
          />
        )}
      </div>
    </AdminLayout>
  );
}
